import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { X, Camera, Trash, Check, ClipboardText } from '@phosphor-icons/react';
import VoiceDictateButton from '../VoiceDictateButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/**
 * Modal shown when a worker (or contractor) marks a task as done.
 * Allows optional note + up to 6 photo proofs.
 * Also used to preview existing note/photos (read-only) when the task is already done.
 *
 * Props:
 *   task        : task object { id, text, done, note, photo_ids }
 *   readOnly    : if true, hides the submit form and shows existing proof
 *   onSubmit(payload)  : called with { done, note, photo_ids } — parent hits the toggle API
 *   onClose()
 */
const TaskCompletionModal = ({ task, readOnly = false, onSubmit, onClose }) => {
  const [note, setNote] = useState(task?.note || '');
  const [photoIds, setPhotoIds] = useState(task?.photo_ids || []);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const photoUrl = (id) => `${API}/daily-checklists/photo/${id}`;

  const pick = () => fileRef.current?.click();

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photoIds.length + files.length > 6) {
      toast({ title: 'Max 6 photos', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const form = new FormData();
        form.append('file', f);
        const res = await axios.post(`${API}/daily-checklists/upload-photo`, form, {
          ...authHeader(),
          headers: { ...authHeader().headers, 'Content-Type': 'multipart/form-data' },
        });
        if (res.data?.file_id) uploaded.push(res.data.file_id);
      }
      setPhotoIds((prev) => [...prev, ...uploaded]);
      if (uploaded.length) toast({ title: `${uploaded.length} photo${uploaded.length !== 1 ? 's' : ''} added` });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removePhoto = (id) => setPhotoIds((prev) => prev.filter((p) => p !== id));

  const submit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({ done: true, note: note.trim() || null, photo_ids: photoIds });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-neutral-900 border-4 border-black shadow-[8px_8px_0px_#000] w-full max-w-md max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="task-completion-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b-4 border-black bg-emerald-500 text-white">
          <div className="flex items-center gap-2 min-w-0">
            <ClipboardText size={20} weight="fill" className="flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-widest opacity-90">
                {readOnly ? 'Task Proof' : 'Mark Done'}
              </div>
              <h3 className="font-black text-base truncate">{task.text}</h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center bg-black text-white border-2 border-white hover:bg-neutral-900 flex-shrink-0"
            data-testid="task-modal-close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {/* Note */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-black uppercase tracking-widest text-neutral-600 dark:text-neutral-400">
                Note (optional)
              </label>
              {!readOnly && (
                <VoiceDictateButton
                  mode="note"
                  compact
                  onResult={(d) => setNote((prev) => ((prev || '').trim() ? `${prev.trim()} ${d.transcript}` : d.transcript))}
                />
              )}
            </div>
            {readOnly ? (
              <p className="mt-1 text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap bg-neutral-50 dark:bg-neutral-800 border-2 border-black p-2 min-h-[3rem]">
                {task.note || <span className="text-neutral-400 italic">No note</span>}
              </p>
            ) : (
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="Anything the contractor should know…"
                className="w-full mt-1 px-3 py-2 border-2 border-black bg-white dark:bg-neutral-800 dark:text-white text-sm shadow-[2px_2px_0px_#000] rounded-none focus:outline-none resize-none"
                data-testid="task-note-input"
              />
            )}
          </div>

          {/* Photos */}
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-neutral-600 dark:text-neutral-400 flex items-center justify-between">
              <span>Photo Proof (optional)</span>
              <span className="text-neutral-500 normal-case tracking-normal">{photoIds.length}/6</span>
            </label>

            {photoIds.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-2" data-testid="task-photo-grid">
                {photoIds.map((id) => (
                  <div key={id} className="relative aspect-square border-2 border-black bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                    <a href={photoUrl(id)} target="_blank" rel="noreferrer">
                      <img src={photoUrl(id)} alt="Task proof" className="w-full h-full object-cover" />
                    </a>
                    {!readOnly && (
                      <button
                        onClick={() => removePhoto(id)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white border-2 border-black flex items-center justify-center hover:bg-red-700"
                        data-testid={`task-photo-remove-${id}`}
                        title="Remove"
                      >
                        <Trash size={12} weight="bold" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {!readOnly && photoIds.length < 6 && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={upload}
                  className="hidden"
                  data-testid="task-photo-input"
                />
                <button
                  onClick={pick}
                  disabled={uploading}
                  className="mt-2 w-full h-11 border-2 border-dashed border-black bg-neutral-50 dark:bg-neutral-800 dark:text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 hover:bg-neutral-100 disabled:opacity-60"
                  data-testid="task-photo-pick-btn"
                >
                  <Camera size={16} weight="bold" />
                  {uploading ? 'Uploading…' : 'Add Photo'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        {!readOnly && (
          <div className="p-4 border-t-4 border-black bg-neutral-50 dark:bg-neutral-800 flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="h-10 px-4 bg-white dark:bg-neutral-900 dark:text-white font-black uppercase text-xs tracking-widest border-2 border-black hover:bg-neutral-100 rounded-none"
              data-testid="task-modal-cancel"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting || uploading}
              className="h-10 px-4 bg-emerald-500 hover:bg-emerald-600 text-white font-black uppercase text-xs tracking-widest border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-0.5 active:shadow-none rounded-none transition-all disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="task-modal-submit"
            >
              <Check size={14} weight="bold" />
              {submitting ? 'Saving…' : 'Mark Done'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskCompletionModal;
